import { useMarketingHistory, useDeleteBanner, useDeleteCampaign, useDuplicateBanner, useDuplicateCampaign } from '../../hooks/useMarketing'
import { Card, CardContent } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { ShareCenter } from '../../components/marketing/ShareCenter'
import { downloadDataUrl } from '../../lib/marketing/share'
import { Copy, Trash2, Download, Image, Megaphone } from 'lucide-react'
import type { MarketingBanner, MarketingCampaign } from '../../lib/marketing/types'

export default function MarketingHistory() {
  const { data, isLoading } = useMarketingHistory()
  const deleteBanner = useDeleteBanner()
  const deleteCampaign = useDeleteCampaign()
  const duplicateBanner = useDuplicateBanner()
  const duplicateCampaign = useDuplicateCampaign()

  if (isLoading) return <div className="text-center py-12 text-gray-500">Memuat riwayat...</div>

  const banners = data?.banners ?? []
  const campaigns = data?.campaigns ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Riwayat Promosi</h1>
        <p className="text-gray-600 mt-1">Kelola semua banner dan campaign yang tersimpan</p>
      </div>

      <section>
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Image className="w-5 h-5" /> Banner ({banners.length})
        </h2>
        {banners.length === 0 ? (
          <p className="text-gray-500 text-sm">Belum ada banner tersimpan</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {banners.map((b: MarketingBanner) => {
              const thumb = b.imageData ? Object.values(b.imageData)[0] : null
              return (
                <Card key={b.id}>
                  <CardContent className="pt-4">
                    {thumb && <img src={thumb} alt={b.title} className="w-full aspect-square object-cover rounded-lg mb-3" />}
                    <h3 className="font-semibold truncate">{b.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{new Date(b.createdAt).toLocaleDateString('id-ID')}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {thumb && (
                        <Button variant="outline" size="sm" onClick={() => downloadDataUrl(thumb, `banner-${b.id}.png`)}>
                          <Download className="w-3 h-3 mr-1" /> Download
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => duplicateBanner.mutate(b.id)}>
                        <Copy className="w-3 h-3 mr-1" /> Duplikat
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => deleteBanner.mutate(b.id)}>
                        <Trash2 className="w-3 h-3 mr-1 text-red-500" /> Hapus
                      </Button>
                    </div>
                    {thumb && <div className="mt-3"><ShareCenter imageDataUrl={thumb} caption={b.title} /></div>}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Megaphone className="w-5 h-5" /> Campaign ({campaigns.length})
        </h2>
        {campaigns.length === 0 ? (
          <p className="text-gray-500 text-sm">Belum ada campaign tersimpan</p>
        ) : (
          <div className="space-y-4">
            {campaigns.map((c: MarketingCampaign) => {
              const thumb = c.banners?.instagram_feed
              const caption = c.captions?.instagram
                ? `${c.captions.instagram}\n\n${c.hashtags || ''}\n\n${c.cta || ''}`
                : c.title
              return (
                <Card key={c.id}>
                  <CardContent className="pt-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {thumb && <img src={thumb} alt={c.title} className="w-32 h-32 object-cover rounded-lg shrink-0" />}
                      <div className="flex-1">
                        <h3 className="font-semibold">{c.title}</h3>
                        <p className="text-xs text-gray-500 mt-1">{new Date(c.createdAt).toLocaleDateString('id-ID')}</p>
                        {c.catalogLink && (
                          <a href={c.catalogLink} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline mt-2 block truncate">
                            {c.catalogLink}
                          </a>
                        )}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {thumb && (
                            <Button variant="outline" size="sm" onClick={() => downloadDataUrl(thumb, `campaign-${c.id}.png`)}>
                              <Download className="w-3 h-3 mr-1" /> Download
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => duplicateCampaign.mutate(c.id)}>
                            <Copy className="w-3 h-3 mr-1" /> Duplikat
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteCampaign.mutate(c.id)}>
                            <Trash2 className="w-3 h-3 mr-1 text-red-500" /> Hapus
                          </Button>
                        </div>
                        <div className="mt-3">
                          <ShareCenter imageDataUrl={thumb} caption={caption} />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}
